import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Search, Loader2, RefreshCw, CheckCircle2, RotateCcw, Mail, AlertTriangle, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const CATEGORY_LABELS = {
  general: "General", billing: "Billing", technical: "Technical", kiosk: "Kiosk / hardware", account: "Account & access",
};
const ROLE_LABELS = { super_duper_admin: "Super-Duper Admin", super_admin: "Super Admin", finance: "Finance", kiosk_manager: "Kiosk Manager" };
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const FILTERS = [["all", "All"], ["open", "Open"], ["resolved", "Resolved"]];

function Avatar({ name }) {
  const init = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white flex items-center justify-center text-xs font-bold shrink-0">{init}</div>;
}

function TicketCard({ t, onToggle, busy }) {
  const [open, setOpen] = useState(false);
  const resolved = t.status === "resolved";
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden" data-testid={`ticket-${t.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left p-4 flex items-start gap-4 hover:bg-slate-50 dark:hover:bg-slate-900/50" data-testid={`ticket-toggle-${t.id}`}>
        <Avatar name={t.name} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-900 dark:text-white truncate">{t.subject}</span>
            <Badge variant="secondary" className="shrink-0">{CATEGORY_LABELS[t.category] || t.category}</Badge>
            {resolved
              ? <Badge className="bg-emerald-600 hover:bg-emerald-600 shrink-0">Resolved</Badge>
              : <Badge variant="outline" className="text-amber-600 border-amber-300 shrink-0">Open</Badge>}
            {t.email_status === "failed" && (
              <span className="inline-flex items-center gap-1 text-xs text-rose-500" title="Support email failed to send — the message is still logged here"><AlertTriangle className="h-3.5 w-3.5" /> Email failed</span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {t.name} · {t.email} · {ROLE_LABELS[t.role] || t.role} · {fmtDateTime(t.created_at)}
          </p>
          {!open && <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 line-clamp-1">{t.message}</p>}
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pl-[68px] space-y-3">
          <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap rounded-lg bg-slate-50 dark:bg-slate-900 p-3">{t.message}</p>
          <div className="flex flex-wrap items-center gap-2">
            <a href={`mailto:${t.email}?subject=Re: ${encodeURIComponent(t.subject)}`} className="inline-flex"><Button size="sm" variant="outline" data-testid={`ticket-reply-${t.id}`}><Mail className="h-4 w-4 mr-1.5" /> Reply by email</Button></a>
            {resolved ? (
              <Button size="sm" variant="outline" onClick={() => onToggle(t, "open")} disabled={busy} data-testid={`ticket-reopen-${t.id}`}><RotateCcw className="h-4 w-4 mr-1.5" /> Reopen</Button>
            ) : (
              <Button size="sm" onClick={() => onToggle(t, "resolved")} disabled={busy} data-testid={`ticket-resolve-${t.id}`}><CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark resolved</Button>
            )}
            {resolved && t.resolved_by && <span className="text-xs text-slate-400">Resolved by {t.resolved_by} · {fmtDateTime(t.resolved_at)}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/support/tickets").then((r) => setTickets(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggle = async (t, status) => {
    setBusy(true);
    try {
      await api.put(`/support/tickets/${t.id}`, { status });
      setTickets((list) => list.map((x) => x.id === t.id ? { ...x, status, resolved_at: status === "resolved" ? new Date().toISOString() : null } : x));
      toast.success(status === "resolved" ? "Ticket marked resolved" : "Ticket reopened");
    } catch { toast.error("Could not update ticket"); }
    finally { setBusy(false); }
  };

  const openCount = tickets.filter((t) => t.status === "open").length;
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return tickets
      .filter((t) => filter === "all" || t.status === filter)
      .filter((t) => !ql || `${t.subject} ${t.message} ${t.name} ${t.email} ${t.category}`.toLowerCase().includes(ql));
  }, [tickets, filter, q]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="support-tickets-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl grid place-items-center text-white" style={{ background: "var(--brand-primary, #4f46e5)" }}><Inbox className="h-5 w-5" /></div>
          <div>
            <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Support Inbox</h1>
            <p className="text-slate-500 mt-0.5">Every Contact Support message is logged here{openCount ? ` · ${openCount} open` : ""}.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input data-testid="support-tickets-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tickets…" className="pl-8 h-9 w-64" />
          </div>
          <Button data-testid="support-tickets-refresh" variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
        </div>
      </div>

      <div className="flex gap-1.5">
        {FILTERS.map(([val, label]) => (
          <button key={val} type="button" data-testid={`ticket-filter-${val}`} onClick={() => setFilter(val)}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${filter === val ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
            {label}{val === "open" && openCount ? ` (${openCount})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-slate-400" /></div>
      ) : filtered.length ? (
        <div className="space-y-3">
          {filtered.map((t) => <TicketCard key={t.id} t={t} onToggle={toggle} busy={busy} />)}
        </div>
      ) : (
        <div className="py-16 text-center text-slate-400" data-testid="support-tickets-empty">
          <Inbox className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>No {filter === "all" ? "" : filter} support tickets{q ? " match your search" : " yet"}.</p>
        </div>
      )}
    </div>
  );
}
